/* 생성 계약. 어느 모델이든 같은 JSON 하나를 보내고, 어휘는 정본 dsl.json 도구(색칠도구)뿐이다. 대조는 validateCalls, 실행은 executeToolCalls */
import { acceptsOf, describe, entriesFor, groupTitle, kindLabel, madeKind, menuLine, schemaOf } from './dsl';
import { executeToolCalls, registryOf } from './executor';
import type { Registry } from './executor';
import { kindOfObject, validateCalls } from './validate';
import { readCurveShape } from '../expr';
import { givenSymbols, readValues } from '../symbols';
import type { DslEntry, MathcoreApp, MathcoreObject, ToolCall, ToolResult } from './types';
import { applyAxesAndGrid, clearScene, engineOf, fitCamera, settleEngine, straightSamples, waitForCurves } from '../style';

const PALETTE = entriesFor('math2d');

/** 개체 이름 어휘를 종류별 풀로 나눈다. 도구의 name 은 그 도구가 만드는 종류의 풀에서, 개체를 가리키는 인자는 그 자리가 받는 종류의 선 개체와 풀에서만 고른다(종류가 틀린 참조를 스키마에서 막는다). 매개변수 기호(소문자, 그리스 문자)와 겹치지 않는다 */
export const NAME_POOLS: Record<string, string[]> = {
  point: [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), 'P1', 'P2', 'P3', 'Q1', 'Q2', 'A1', 'A2', 'B1', 'B2'],
  linear: ['l1', 'l2', 'l3', 'm1', 'm2'],
  vector: ['l1', 'l2', 'l3', 'm1', 'm2'],
  circle: ['C1', 'C2', 'C3'],
  polynomial: ['f', 'g', 'h', 'f1', 'f2', 'f3', 'g1', 'g2', 'h1', 'h2'],
  polygon: ['S1', 'S2', 'T1', 'T2'],
};
/** 풀 전체. 종류를 모르는 자리와 변환, 측정, 꾸미기의 이름에 쓴다 */
export const NAMES: string[] = [...new Set(Object.values(NAME_POOLS).flat())];
/** 이미 선 개체의 별칭과 종류. 종류를 모르면 빈 글자(모든 자리에서 고를 수 있다) */
export type StandingRef = string | { id: string; kind: string };
const AXES = ['0:xAxis', '0:yAxis'];

/** 비워 둘 수 있는 자리. 모든 자리를 required 에 넣으므로 선택 인자는 null 을 허용한다 */
const nullable = (schema: Record<string, unknown>): Record<string, unknown> => ({ anyOf: [schema, { type: 'null' }] });

/** 도구 하나의 호출 모양. tool 은 const, args 는 그 도구의 인자만(전부 required, 선택 인자는 null 허용, 그 밖은 금지). 참조는 그 자리가 받는 종류의 선 개체와 이름 풀, name 은 만드는 종류의 풀(슬라이더는 문항의 기호), 식은 제시된 식 enum */
type RefShape = (kinds: string[] | undefined) => Record<string, unknown>;
type NameShape = (entry: DslEntry) => Record<string, unknown>;
function callShape(entry: DslEntry, formula: Record<string, unknown>, condition: Record<string, unknown> | null, refOf: RefShape = () => ({ $ref: '#/$defs/ref' }), nameOf: NameShape = () => ({ $ref: '#/$defs/name' })): Record<string, unknown> {
  const accepts = acceptsOf(entry);
  const properties: Record<string, unknown> = {};
  for (const p of entry.params) {
    let shape: Record<string, unknown>;
    if (p.type === 'expr') shape = formula;
    else if (p.type === 'ref') shape = refOf(accepts.get(p.name));
    else if (p.type.startsWith('ref[')) shape = { type: 'array', items: refOf(accepts.get(p.name)) };
    else if (p.name === 'name') shape = nameOf(entry);
    // mathcore 의 number|string 은 슬라이더 연결식용이다. 계약에서는 문자를 symbols 로 대입하므로 수만 받는다
    else if (p.type === 'number|string') shape = { type: 'number' };
    else shape = schemaOf(p.type);
    properties[p.name] = p.optional ? nullable(shape) : shape;
  }
  // condition 은 계약의 인자. 식에 붙은 조건 식(|x| \le 1)이고 대조가 식에 합친다. 제시된 조건 식이 없으면 자리를 두지 않는다
  if (entry.params.some((p) => p.type === 'expr') && condition) properties.condition = nullable(condition);
  return {
    type: 'object',
    properties: { tool: { type: 'string', const: entry.name }, args: { type: 'object', properties, required: Object.keys(properties), additionalProperties: false } },
    required: ['tool', 'args'],
    additionalProperties: false,
  };
}

/** 제시된 식을 코드가 읽는 대로 가른다: 곡선으로 읽히는 것은 expression 자리, x 의 구간으로 읽히는 것은 condition 자리. 모델이 둘을 바꿔 넣을 수 없다.
 * 값 없는 기호가 든 식도 구조가 읽히면 고를 수 있다(값은 symbols 로 준다) */
export function splitGiven(given: string[]): { curves: string[]; conditions: string[] } {
  const curves = given.filter((g) => { const r = readCurveShape(g); return r.latex !== null || Boolean(r.pieces?.length); });
  const conditions = given.filter((g) => Boolean(readCurveShape('y=x', g).pieces?.length));
  return { curves, conditions };
}

/** 모델이 보내는 JSON 의 스키마. calls 의 항목은 도구 모양 중 하나(anyOf), symbols 는 식에 남는 기호의 값.
 * tools 가 있으면 취합한 도구의 모양만, 없으면 팔레트 전부다. 사용법과 args 단계는 취합한 도구만 받는다.
 * given 이 있으면 식(expression)과 조건(condition)은 그 목록(문항과 풀이에 제시된 식) 안에서만 고른다. 모델은 식을 쓰지 않고 쓸 식을 고른다.
 * symbolNames 가 있으면 symbols 의 이름은 그 안에서만 고른다(제시된 식이 쓰는 기호 전부). 없으면 문자열이고 서버가 정규 이름으로 읽는다 */
export function callsSchema(given: string[] = [], symbolNames: string[] = [], tools?: readonly string[], strictSymbols = false, standing: readonly StandingRef[] = []): Record<string, unknown> {
  const palette = tools ? PALETTE.filter((e) => tools.includes(e.name)) : PALETTE;
  const split = splitGiven(given);
  const symbolName = symbolNames.length ? { type: 'string', enum: symbolNames } : { type: 'string' };
  const formula = split.curves.length ? { type: 'string', enum: split.curves } : { type: 'string' };
  const condition = split.conditions.length ? { type: 'string', enum: split.conditions } : null;
  const stand = standing.map((s) => (typeof s === 'string' ? { id: s, kind: '' } : s));
  // 이름 어휘는 한 번만 적고 $ref 로 가리킨다(도구마다 펼치면 스키마가 84k자가 된다)
  const defs: Record<string, unknown> = {
    name: { type: 'string', enum: NAMES },
    ref: { type: 'string', enum: [...stand.map((s) => s.id), ...NAMES], description: '이미 선 개체의 id, 또는 앞 호출이 세운 name' },
  };
  // 받는 종류가 정해진 자리: 그 종류의 선 개체(별칭)와 그 종류의 이름 풀만 고른다. 좌표축은 axis 를 받는 자리에서만. 고를 것이 없는 종류(타원, 현 등)는 일반 참조로 둔다
  const idsOf = (kinds: string[]): Record<string, unknown> | null => {
    const key = `ref_${[...kinds].sort().join('_')}`;
    if (!defs[key]) {
      const ids = [...new Set([...stand.filter((s) => s.kind === '' || kinds.includes(s.kind)).map((s) => s.id), ...(kinds.includes('axis') ? AXES : []), ...kinds.flatMap((k) => NAME_POOLS[k] ?? [])])];
      if (!ids.length) return null;
      defs[key] = { type: 'string', enum: ids, description: `${kinds.map(kindLabel).join(' 또는 ')} 인 선 개체의 id, 또는 앞 호출이 그 종류로 세운 name` };
    }
    return { $ref: `#/$defs/${key}` };
  };
  // 자리 명세: 받는 개체가 아직 없으면 모델은 그 자리에 뜻만 적고(좌표, 어느 곡선 위, 두 점, 식), 그 개체를 세우는 호출과 순서는 코드가 만든다(expandSpecs). 색칠도구의 조합과 시나리오는 코드에 고정한다(사용자 정의 2026-10-16)
  const slotPoint = () => { if (!defs.slot_point) { const curveRef = idsOf(['polynomial', 'circle', 'linear']); defs.spec_point = { type: 'object', description: '새로 세울 점: 좌표. on 이 있으면 그 개체 위의 점(좌표는 어림 위치)', properties: { x: { type: 'number' }, y: { type: 'number' }, on: nullable(curveRef ?? { type: 'string' }) }, required: ['x', 'y', 'on'], additionalProperties: false }; defs.slot_point = { anyOf: [idsOf(['point']) ?? { type: 'string' }, { $ref: '#/$defs/spec_point' }] }; } return { $ref: '#/$defs/slot_point' }; };
  const specOf = (kind: string): Record<string, unknown> | null => {
    if (kind === 'point') return { $ref: '#/$defs/spec_point' };
    if (kind === 'linear') { if (!defs.spec_linear) { slotPoint(); defs.spec_linear = { type: 'object', description: '새로 세울 선: 두 점을 잇는 선분', properties: { through: { type: 'array', items: { $ref: '#/$defs/slot_point' }, minItems: 2, maxItems: 2 } }, required: ['through'], additionalProperties: false }; } return { $ref: '#/$defs/spec_linear' }; }
    if (kind === 'polynomial') { if (!defs.spec_polynomial) defs.spec_polynomial = { type: 'object', description: '새로 세울 곡선: 제시된 식과 조건', properties: { expression: formula, condition: nullable(condition ?? { type: 'string' }) }, required: ['expression', 'condition'], additionalProperties: false }; return { $ref: '#/$defs/spec_polynomial' }; }
    if (kind === 'circle') { if (!defs.spec_circle) { slotPoint(); defs.spec_circle = { type: 'object', description: '새로 세울 원: 중심과 반지름', properties: { center: { $ref: '#/$defs/slot_point' }, radius: { type: 'number' } }, required: ['center', 'radius'], additionalProperties: false }; } return { $ref: '#/$defs/spec_circle' }; }
    return null;
  };
  const refOf: RefShape = (kinds) => {
    if (!kinds) return { $ref: '#/$defs/ref' };
    if (kinds.includes('point')) slotPoint();
    const ids = idsOf(kinds) ?? { $ref: '#/$defs/ref' };
    const specs = kinds.map(specOf).filter((s): s is Record<string, unknown> => s !== null);
    return specs.length ? { anyOf: [ids, ...specs] } : ids;
  };
  // name: 만드는 종류의 풀. 변환(원본 종류를 따름), 측정, 꾸미기는 풀 전체. 슬라이더는 식의 기호 이름이어야 연결되므로 문항의 기호에서 고른다
  const nameOf: NameShape = (entry) => {
    if (entry.name === 'etc.slider' && symbolNames.length) return { type: 'string', enum: symbolNames };
    const pool = NAME_POOLS[madeKind(entry)];
    return pool && !entry.name.startsWith('transform.') ? { type: 'string', enum: pool } : { $ref: '#/$defs/name' };
  };
  return {
    type: 'object',
    $defs: defs,
    properties: {
      symbols: {
        type: 'array',
        description: '식에 남는 기호(m, a, k, 그리스 문자 \\alpha \\theta, 아래첨자 a_1 …)와 그림에 쓸 값. 이름은 문항의 표기 그대로(alpha, \\alpha, α 는 같다). 풀이에서 값이 정해졌으면 그 값. 식에는 기호를 그대로 두고 여기서 값을 준다. 값 없는 기호가 식에 남으면 그 호출은 반려된다. 없으면 빈 배열',
        // strictSymbols 이고 값을 선언할 기호가 없으면 선언 자체를 막는다(풀이 글의 작업 변수가 기호로 새지 않게)
        maxItems: strictSymbols && symbolNames.length === 0 ? 0 : 12,
        items: { type: 'object', properties: { name: symbolName, value: { type: 'number' } }, required: ['name', 'value'], additionalProperties: false },
      },
      calls: {
        type: 'array',
        description: '순서대로 실행한다. 뒤 호출은 앞 호출이 세운 name 을 가리킬 수 있다. 문항의 그림에 필요한 개체만 적는다',
        // 상한: 그림 하나의 개체 수(곡선, 점, 선, 꾸미기, 측정까지)
        maxItems: palette.length ? 64 : 0,
        items: palette.length ? { anyOf: palette.map((e) => callShape(e, formula, condition, refOf, nameOf)) } : { type: 'object' },
      },
    },
    required: ['symbols', 'calls'],
    additionalProperties: false,
  };
}

export const CALLS_SCHEMA: Record<string, unknown> = callsSchema();

/** 색칠도구 설명 글. 도구 이름 뒤에 mathcore 서명(함수, 인자 형, 만드는 것)과 앞에 있어야 하는 것을 그대로 싣는다. names 가 있으면 그 도구만(3단계: 취합한 도구의 사용법) */
export function paletteText(names?: readonly string[]): string {
  return PALETTE.filter((e) => !names || names.includes(e.name)).map((e) => {
    const [name, ...rest] = describe(e).split('\n');
    return `- ${name}\n${rest.map((l) => `  ${l}`).join('\n')}`;
  }).join('\n');
}

/** 지금 화면에서 모델에게 보일 개체. 이름표 글자와 무효 개체는 뺀다 */
export function standingObjects(app: MathcoreApp): MathcoreObject[] {
  return (app.objectManager?.getAllObjects?.() ?? []).filter((o) => o.name && !String(o.name).startsWith('nametext_') && o.isValid !== false);
}

/** 모델이 개체를 가리키는 별칭(o1, o2 …). 개체를 가리키는 인자는 이 별칭으로 가리킨다. mathcore 의 개체 번호는 화면에서 이어 세어져 실행마다 달라지므로 모델 입력에 싣지 않는다 */
export function standingIds(app: MathcoreApp): string[] {
  const registry = registryOf(app);
  return standingObjects(app).map((o) => registry.aliasOf(o.id));
}

/** 이미 선 개체의 별칭과 종류. 스키마가 받는 종류별로 참조 enum 을 짠다 */
export function standingRefs(app: MathcoreApp): { id: string; kind: string }[] {
  const registry = registryOf(app);
  return standingObjects(app).map((o) => ({ id: registry.aliasOf(o.id), kind: kindOfObject(o as { type?: string; inequalityExists?: boolean }) }));
}

/** 정본 math2d 팔레트의 도구 이름 전부 */
export const TOOL_NAMES: string[] = PALETTE.map((e) => e.name);

/** 고른 도구가 받는 종류를 세우는 도구를 같은 단계에 함께 싣는다. A 를 만들려면 Z 가 먼저 있어야 하는데 Z 를 세울 도구가 없어 되묻기로 가던 구멍을 막는다. 종류마다 정본의 도구 하나 */
const PRODUCERS: Record<string, string> = { point: 'point.basic', linear: 'linear.segment', circle: 'circle.circleWithCenterAndRadius', polygon: 'polygon.polygon', vector: 'linear.vector', polynomial: 'algebraric' };
export function withProducers(tools: readonly string[]): string[] {
  const out = [...tools];
  for (let i = 0; i < out.length; i += 1) {
    const entry = PALETTE.find((e) => e.name === out[i]);
    if (!entry) continue;
    for (const kinds of acceptsOf(entry).values()) for (const k of kinds) { const p = PRODUCERS[k]; if (p && TOOL_NAMES.includes(p) && !out.includes(p)) out.push(p); }
  }
  return out;
}

/** 입력 단계의 도구: 식, 좌표, 값만 받는 개체와 그 값의 기호를 묶는 슬라이더. 다른 개체를 가리키는 인자가 없는 것만 둔다(아래에서 확인한다) */
export const INPUT_TOOLS: string[] = ['algebraric', 'point.basic', 'etc.slider'];
for (const name of INPUT_TOOLS) {
  const entry = PALETTE.find((e) => e.name === name);
  if (!entry || entry.params.some((p) => p.type === 'ref' || p.type.startsWith('ref['))) throw new Error(`입력 도구 ${name} 이 정본에 없거나 다른 개체를 가리키는 인자를 가졌다`);
}

/** 연계 단계를 의존 순서의 작은 단계로 가른다: 구성(점, 선, 원, 다각형, 변환), 측정, 꾸미기. 한 단계가 끝나 개체가 선 뒤에야 다음 단계가 그 개체를 가리킨다.
 * 수능 그림은 종이에 찍히는 정적인 그림이라 꾸미기는 설명선뿐이고, 움직이거나 켜고 끄는 도구(모션, 체크박스)는 메뉴에 두지 않는다(사용자 지시 2026-10-16). 구성과 측정의 묶음은 정본 도구 경로의 첫 마디다 */
const rootOf = (name: string): string => (PALETTE.find((e) => e.name === name)?.path ?? [name])[0];
const DECORATION_TOOLS = ['deco.annotationBySegment', 'deco.annotationByTwoPoints'];
const missing = DECORATION_TOOLS.filter((n) => !TOOL_NAMES.includes(n));
if (missing.length) throw new Error(`꾸미기 도구가 정본에 없다: ${missing.join(', ')}`);
const LINK_LAYOUT: { key: string; roots?: string[]; names?: string[] }[] = [
  { key: '구성', roots: ['point', 'linear', 'circle', 'polygon', 'transform'] },
  { key: '측정', roots: ['measure'] },
  { key: '꾸미기', names: DECORATION_TOOLS },
];
const layoutTools = (l: { roots?: string[]; names?: string[] }): string[] => TOOL_NAMES.filter((n) => !INPUT_TOOLS.includes(n) && (l.names ? l.names.includes(n) : (l.roots ?? []).includes(rootOf(n))));
export const LINK_PHASES: { key: string; tools: string[] }[] = LINK_LAYOUT.map((l) => ({ key: l.key, tools: layoutTools(l) }));

/** 연계 단계의 도구: 단계에 든 도구 전부. 대부분 인자로 다른 개체를 가리키고, mathcore 가 관련 개체(RO)로 묶어 계산한다 */
export const LINK_TOOLS: string[] = LINK_PHASES.flatMap((p) => p.tools);

/** 팔레트에 있는 이름만, 중복 없이 */
export function inPalette(names: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const n of names) if (typeof n === 'string' && TOOL_NAMES.includes(n) && !out.includes(n)) out.push(n);
  return out;
}

/** 2단계 취합의 메뉴: 정본 경로의 첫 마디(point, linear, measure …)로 묶고, 도구마다 이름, 한글 이름, 설명, 만드는 개체를 한 줄씩. 사용법은 3단계에서 취합한 도구만 받는다 */
export function menuText(names?: readonly string[]): string {
  const groups = new Map<string, DslEntry[]>();
  for (const e of PALETTE.filter((one) => !names || names.includes(one.name))) { const key = (e.path ?? [e.name])[0]; groups.set(key, [...(groups.get(key) ?? []), e]); }
  return [...groups].map(([key, list]) => {
    const titles = [...new Set(list.map(groupTitle).filter(Boolean))];
    return `## ${key}${titles.length ? ` (${titles.join(', ')})` : ''}\n${list.map(menuLine).join('\n')}`;
  }).join('\n');
}

/** 2단계 취합의 출력 스키마: 도구 이름의 배열 하나 */
export function pickSchema(names: readonly string[] = TOOL_NAMES): Record<string, unknown> {
  return {
    type: 'object',
    properties: {
      tools: { type: 'array', description: '그림에 더 쓸 색칠도구의 이름. 목록에 있는 이름만, 같은 이름은 한 번만. 더 필요한 도구가 없으면 빈 배열', maxItems: 16, items: { type: 'string', enum: [...names] } },
    },
    required: ['tools'],
    additionalProperties: false,
  };
}

/** 취합 출력 → 팔레트에 있는 도구 이름들 */
export function readPicked(raw: unknown, allowed: readonly string[] = TOOL_NAMES): string[] {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { tools?: unknown }).tools) ? (raw as { tools: unknown[] }).tools : [];
  return inPalette(list).filter((n) => allowed.includes(n));
}

/** MCP 생성 입구의 스키마. 도구 이름만 enum 이고 args 의 모양은 tool_usage 가 준 정의를 따른다. 모양 대조는 validateCalls 가 한다 */
export function lightCallsSchema(): Record<string, unknown> {
  const full = callsSchema().properties as Record<string, Record<string, unknown>>;
  return {
    type: 'object',
    properties: {
      symbols: full.symbols,
      calls: {
        type: 'array',
        description: '순서대로 실행한다. tool 은 tool_menu 로 취합한 도구의 이름, args 는 tool_usage 가 준 그 도구의 args 정의대로. 뒤 호출은 앞 호출이 세운 name 을 가리킬 수 있다',
        maxItems: 64,
        items: { type: 'object', properties: { tool: { type: 'string', enum: TOOL_NAMES }, args: { type: 'object' } }, required: ['tool', 'args'], additionalProperties: false },
      },
    },
    required: ['symbols', 'calls'],
    additionalProperties: false,
  };
}

/** 3단계: 취합한 도구의 사용법과 args 정의. 설명은 describe, args 정의는 데모의 스키마와 같은 callShape 에서 뽑는다 */
export function usageText(names: readonly string[]): string {
  const formula = { type: 'string', description: 'problem 과 solution 에 $…$ 로 적힌 식 중 하나를 글자 그대로' };
  const condition = { type: 'string', description: '그 식에 붙은 조건 식. 없으면 null' };
  const blocks = PALETTE.filter((e) => names.includes(e.name)).map((e) => {
    const args = (callShape(e, formula, condition).properties as Record<string, unknown>).args;
    return `## ${describe(e)}\nargs 정의(JSON Schema): ${JSON.stringify(args)}`;
  });
  return [`name 은 이 이름 중에서 고른다: ${NAMES.join(' ')}\n개체를 가리키는 인자($ref)는 read_scene 이나 draw_calls 결과가 준 개체의 id, 또는 앞 호출이 세운 name 이다`, ...blocks].join('\n\n');
}

export interface Drawn {
  /** 대조에서 거른 호출과 사유 */
  rejected: { tool: string; detail: string; name: string }[];
  results: ToolResult[];
  /** 덧그릴 때 이미 선 이름이라 건너뛴 호출 */
  skipped: string[];
  /** 읽지 못해 버린 값 선언의 사유. 조용히 버리지 않고 보고한다 */
  issues: string[];
}

/** 참조 순서는 코드가 정한다: 어떤 호출이 가리키는 name 을 뒤의 호출이 세우면 그 호출을 앞으로 옮긴다(의존 정렬). 같은 JSON 안의 순서를 모델에게 맡기지 않는다 */
export function orderCalls(calls: ToolCall[]): ToolCall[] {
  const parsed = calls.map((c) => { let args: Record<string, unknown> = {}; try { args = JSON.parse(c.function.arguments || '{}'); } catch { args = {}; } return { call: c, args, name: typeof args.name === 'string' ? args.name : '' }; });
  const refsOf = (args: Record<string, unknown>, tool: string): string[] => {
    const entry = PALETTE.find((e) => e.name === tool); if (!entry) return [];
    const out: string[] = [];
    for (const p of entry.params) { const v = args[p.name]; if (p.type === 'ref' && typeof v === 'string') out.push(v); if (p.type.startsWith('ref[') && Array.isArray(v)) for (const one of v) if (typeof one === 'string') out.push(one); }
    return out;
  };
  const placed: typeof parsed = []; const done = new Set<number>(); const visiting = new Set<number>();
  // 세우는 호출은 자기 자신이 아닌 것 중에서 찾는다. 호출의 name 이 자기가 가리키는 이름과 같아도 그 이름을 세우는 다른 호출이 앞으로 간다
  const creator = (name: string, self: number) => parsed.findIndex((p, k) => k !== self && p.name === name);
  const visit = (i: number) => {
    if (done.has(i) || visiting.has(i)) return;
    visiting.add(i);
    for (const r of refsOf(parsed[i].args, parsed[i].call.function.name)) { const j = creator(r, i); if (j >= 0) visit(j); }
    visiting.delete(i); done.add(i); placed.push(parsed[i]);
  };
  parsed.forEach((_, i) => visit(i));
  return placed.map((p) => p.call);
}

/** 자리 명세를 선행 호출로 푼다. 점 {x,y,on}, 선 {through:[점,점]}, 곡선 {expression,condition}, 원 {center,radius} 가 개체 자리에 오면 그 개체를 세우는 호출을 앞에 끼우고 자리에는 새 이름을 넣는다.
 * 이름은 그 종류의 풀에서 아직 안 쓴 것. 선행 개체를 세우는 도구, 인자 자리, 순서는 여기 고정이다(색칠도구의 조합과 시나리오는 코드 몫, 사용자 정의 2026-10-16) */
export function expandSpecs(raw: unknown, known: readonly string[] = []): unknown {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { calls?: unknown }).calls) ? (raw as { calls: unknown[] }).calls : null;
  if (!list) return raw;
  const used = new Set<string>(known);
  for (const c of list) { const n = (c as { args?: { name?: unknown } })?.args?.name; if (typeof n === 'string') used.add(n); }
  const fresh = (kind: string): string => { const pool = NAME_POOLS[kind] ?? NAMES; const pick = pool.find((n) => !used.has(n)) ?? `${kind}${used.size}`; used.add(pick); return pick; };
  const out: { tool: string; args: Record<string, unknown> }[] = [];
  const isSpec = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
  const place = (v: unknown, kinds: string[] | undefined): unknown => {
    if (!isSpec(v)) return v;
    const k = kinds ?? [];
    if ('through' in v && Array.isArray(v.through)) { const [a, b] = v.through.map((p) => place(p, ['point'])); const name = fresh('linear'); out.push({ tool: 'linear.segment', args: { pointID1: a, pointID2: b, name } }); return name; }
    if ('expression' in v) { const name = fresh('polynomial'); out.push({ tool: 'algebraric', args: { expression: v.expression, name, condition: v.condition ?? null } }); return name; }
    if ('center' in v) { const center = place(v.center, ['point']); const name = fresh('circle'); out.push({ tool: 'circle.circleWithCenterAndRadius', args: { pointID: center, radius: v.radius, name } }); return name; }
    if ('x' in v && 'y' in v) { const name = fresh('point'); if (v.on) out.push({ tool: 'point.pointOnObject', args: { objectId: v.on, position: [v.x, v.y], name } }); else out.push({ tool: 'point.basic', args: { x: v.x, y: v.y, name } }); return name; }
    return k.length ? v : v;
  };
  for (const c of list) {
    if (!c || typeof c !== 'object') continue;
    const { tool, args } = c as { tool?: unknown; args?: unknown };
    if (typeof tool !== 'string') continue;
    const entry = PALETTE.find((e) => e.name === tool);
    const a: Record<string, unknown> = { ...(args && typeof args === 'object' && !Array.isArray(args) ? args as Record<string, unknown> : {}) };
    if (entry) for (const p of entry.params) {
      const kinds = acceptsOf(entry).get(p.name);
      if (p.type === 'ref') a[p.name] = place(a[p.name], kinds);
      else if (p.type.startsWith('ref[') && Array.isArray(a[p.name])) a[p.name] = (a[p.name] as unknown[]).map((v) => place(v, kinds));
    }
    out.push({ tool, args: a });
  }
  return { ...(raw as Record<string, unknown>), calls: out };
}

/** {calls:[{tool, args}]} → ToolCall[]. 모양이 아닌 항목은 버린다 */
export function readCalls(raw: unknown): ToolCall[] {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { calls?: unknown }).calls) ? (raw as { calls: unknown[] }).calls : [];
  return list.flatMap((c, i) => {
    if (!c || typeof c !== 'object') return [];
    const { tool, args } = c as { tool?: unknown; args?: unknown };
    if (typeof tool !== 'string') return [];
    const argObject = args && typeof args === 'object' && !Array.isArray(args) ? args : {};
    return [{ id: `call-${i}`, type: 'function' as const, function: { name: tool, arguments: JSON.stringify(argObject) } }];
  });
}

/** {symbols:[{name, value}]} → 기호 값(엔진의 정규 이름). 읽지 못한 선언은 버리지 않고 issues 에 사유를 남긴다 */
export function readSymbolValues(raw: unknown, known: readonly string[] = []): { values: Record<string, number>; issues: string[] } {
  const read = readValues(raw && typeof raw === 'object' ? (raw as { symbols?: unknown }).symbols : undefined, known);
  return { values: read.values, issues: read.issues };
}

export function readSymbols(raw: unknown, known: readonly string[] = []): Record<string, number> {
  return readSymbolValues(raw, known).values;
}

/** 생성 입구. append 가 거짓이면 화면을 비우고 그린다. given 이 있으면 식은 그 목록 안의 것만 받는다. 기호는 symbols 의 값을 대입한다(symbolNames: 호출자가 이미 아는 정규 이름). 결과는 호출마다 실행기 문장 */
/** 실행 직전 카메라 맞춤의 채움 비율. 캡처(0.7)보다 넓게 본다 */
const EXECUTE_FILL = 0.35;

/** 참조(id, 별칭, 등록부 이름)가 가리키는 화면 개체 */
function resolveRef(app: MathcoreApp, registry: Registry, ref: string): MathcoreObject | undefined {
  const all = (app.objectManager?.getAllObjects?.() ?? []) as MathcoreObject[];
  const byId = all.find((o) => o.id === ref);
  if (byId) return byId;
  const byAlias = all.find((o) => registry.aliasOf(o.id) === ref);
  if (byAlias) return byAlias;
  const live = registry.objectOf(ref);
  return live ? all.find((o) => o.id === live.id) : undefined;
}

/** 선 자리에 직선꼴 대수 곡선(y=x 같은 1차식)이 오면 반려하지 않고 곡선 위 두 점과 linear.line 으로 바꿔 넣는다.
 * 팔레트에는 식으로 직선을 세우는 도구가 없어 모델이 고칠 수 없는 반려가 되풀이됐다(실측 2026-10-19: 8건 전부 이 사유). 보조 점은 세운 뒤 숨긴다 */
function adaptLinearSlots(app: MathcoreApp, registry: Registry, calls: ToolCall[], kinds: ReadonlyMap<string, string>): { calls: ToolCall[]; helpers: string[]; notices: string[] } {
  const out: ToolCall[] = [];
  const helpers: string[] = [];
  const notices: string[] = [];
  const lineOf = new Map<string, string>();
  let seq = 0;
  for (const call of calls) {
    const entry = PALETTE.find((e) => e.name === call.function.name);
    let args: Record<string, unknown> = {};
    try { args = JSON.parse(call.function.arguments || '{}'); } catch { out.push(call); continue; }
    if (!entry) { out.push(call); continue; }
    let changed = false;
    for (const p of entry.params) {
      if (p.type !== 'ref') continue;
      const allowed = acceptsOf(entry).get(p.name);
      if (!allowed || !allowed.includes('linear') || allowed.includes('polynomial')) continue;
      const ref = args[p.name];
      if (typeof ref !== 'string' || kinds.get(ref) !== 'polynomial') continue;
      if (!lineOf.has(ref)) {
        const target = resolveRef(app, registry, ref);
        const pts = target ? straightSamples(app, target.id) : null;
        if (!target || !pts) continue;
        seq += 1;
        const p1 = `h${seq}a`; const p2 = `h${seq}b`; const line = `h${seq}l`;
        const first = pts[Math.floor(pts.length * 0.25)]; const last = pts[Math.floor(pts.length * 0.75)];
        out.push({ id: `${call.id}-h${seq}-1`, type: 'function', function: { name: 'point.pointOnObject', arguments: JSON.stringify({ objectId: ref, position: [first[0], first[1]], name: p1 }) } });
        out.push({ id: `${call.id}-h${seq}-2`, type: 'function', function: { name: 'point.pointOnObject', arguments: JSON.stringify({ objectId: ref, position: [last[0], last[1]], name: p2 }) } });
        out.push({ id: `${call.id}-h${seq}-3`, type: 'function', function: { name: 'linear.line', arguments: JSON.stringify({ pointID1: p1, pointID2: p2, name: line }) } });
        helpers.push(p1, p2);
        lineOf.set(ref, line);
        notices.push(`${call.function.name}: ${p.name} 자리의 "${ref}" 은 직선꼴 대수 곡선이라 그 위 두 점으로 직선 ${line} 을 세워 대신 넣는다`);
      }
      args[p.name] = lineOf.get(ref);
      changed = true;
    }
    out.push(changed ? { ...call, function: { ...call.function, arguments: JSON.stringify(args) } } : call);
  }
  return { calls: out, helpers, notices };
}

/** 보조 점과 그 이름표를 숨긴다(원개체 visible. 점에서는 먹힌다, 실측 2026-10-19) */
function hideHelpers(app: MathcoreApp, names: string[]): void {
  if (!names.length) return;
  const manager: any = engineOf(app)?.objectManager;
  const all = (app.objectManager?.getAllObjects?.() ?? []) as MathcoreObject[];
  for (const o of all) {
    const n = String(o.name ?? '');
    if (!names.includes(n) && !names.includes(n.replace(/^nametext_/, ''))) continue;
    const raw = manager?.getObject?.(o.id);
    if (raw) raw.visible = false;
  }
  try { engineOf(app)?.render?.(); } catch { /* 비공개 경로 실패는 넘어간다 */ }
}

export async function drawCalls(app: MathcoreApp, raw: unknown, append = false, given?: string[], symbolNames: string[] = [], afterEach?: (names: string[]) => Promise<void>): Promise<Drawn> {
  if (!append) await clearScene(app);
  const standing = append ? (app.objectManager?.getAllObjects?.() ?? []) : [];
  const registry = registryOf(app);
  // 이미 선 이름: 화면의 이름과, 요청한 이름으로 만든 개체의 등록부 이름(조각으로 나뉜 곡선도 요청한 이름으로 가리킨다)
  // 참조로 인정하는 것: 개체의 id 와 별칭, 같은 묶음에서 요청한 이름. mathcore 가 자동으로 붙인 화면의 이름은 인정하지 않는다
  const known = [...new Set([...standing.map((o) => o.id), ...standing.map((o) => registry.aliasOf(o.id)), ...registry.knownNames()])].filter(Boolean);
  const sliders = standing.filter((o) => o.type === 'slider').map((o) => o.name ?? '').filter(Boolean);
  // 덧그릴 때 이미 선 이름이면: 같은 종류의 개체를 다시 세우는 호출은 건너뛰고(되먹임에서 모델이 앞 호출을 되풀이해도 겹치지 않는다), 다른 종류가 그 이름을 쓰려 하면 반려한다
  const all = readCalls(expandSpecs(raw, known));
  const nameOf = (c: ToolCall): string => { try { const n = JSON.parse(c.function.arguments).name; return typeof n === 'string' ? n : ''; } catch { return ''; } };
  const standingKind = new Map<string, string>();
  for (const n of registry.knownNames()) if (!standingKind.has(n)) { const o = registry.objectOf(n); if (o?.type) standingKind.set(n, kindOfObject(o as { type?: string; inequalityExists?: boolean })); }
  const kindOf = (c: ToolCall): string => { const e = PALETTE.find((x) => x.name === c.function.name); return e ? madeKind(e) : ''; };
  const taken = (c: ToolCall): boolean => append && nameOf(c) !== '' && known.includes(nameOf(c));
  const clashed = all.filter((c) => taken(c) && kindOf(c) !== '' && standingKind.get(nameOf(c)) !== kindOf(c));
  // 이미 선 이름을 다른 종류가 쓰려 하면 되묻지 않고 이름을 떼어 별칭으로만 세운다(앞 개체가 이름을 지킨다)
  const unnamed = (c: ToolCall): ToolCall => { let a: Record<string, unknown> = {}; try { a = JSON.parse(c.function.arguments || '{}'); } catch { a = {}; } delete a.name; return { ...c, function: { ...c.function, arguments: JSON.stringify(a) } }; };
  const fresh = all.filter((c) => !taken(c) || clashed.includes(c)).map((c) => (clashed.includes(c) ? unnamed(c) : c));
  const skipped = [
    ...all.filter((c) => taken(c) && !clashed.includes(c)).map((c) => `${c.function.name} ${nameOf(c)}: 이미 선 이름이라 건너뜀`),
    ...clashed.map((c) => `${c.function.name} ${nameOf(c)}: 이름 "${nameOf(c)}" 은 이미 ${standingKind.get(nameOf(c))} 개체가 쓴다. 이름 없이(별칭으로만) 세움`),
  ];
  const symbols = readSymbolValues(raw, [...(given ? givenSymbols(given) : []), ...symbolNames]);
  const kinds = new Map<string, string>([...standingKind].filter(([n, t]) => n && t));
  for (const o of standing) if (o.id && o.type) { const kind = kindOfObject(o as { type?: string; inequalityExists?: boolean }); kinds.set(o.id, kind); kinds.set(registry.aliasOf(o.id), kind); }
  const adapted = adaptLinearSlots(app, registry, orderCalls(fresh), kinds);
  const checked = validateCalls(adapted.calls, known, symbols.values, given, sliders, kinds);
  // 실행 직전 카메라를 넓게 맞춘다. 엔진은 접점과 교점을 보이는 창 안에서만 찾으므로 창 밖의 답은 무효로 선다(실측 2026-10-20). 이번에 세울 좌표 점도 창에 넣는다
  const coming: [number, number][] = [];
  for (const c of checked.calls) { try { const a = JSON.parse(c.function.arguments || '{}'); if (c.function.name === 'point.basic' && Number.isFinite(a.x) && Number.isFinite(a.y)) coming.push([a.x, a.y]); if (Array.isArray(a.position) && Number.isFinite(a.position[0]) && Number.isFinite(a.position[1])) coming.push([a.position[0], a.position[1]]); } catch { /* 인자 오류는 실행기가 보고한다 */ } }
  if (checked.calls.length) { await waitForCurves(app); fitCamera(app, EXECUTE_FILL, coming); await settleEngine(app); }
  const results = checked.calls.length ? await executeToolCalls(app, checked.calls, afterEach) : [];
  await settleEngine(app);
  hideHelpers(app, adapted.helpers);
  applyAxesAndGrid(app, true, true);
  // 보조 호출(h1a, h1b, h1l)은 모델의 호출이 아니므로 결과 목록에서 뺀다
  const own = results.filter((r) => !adapted.helpers.includes(nameOf(r.call)) && !/^h\d+l$/.test(nameOf(r.call)));
  return { rejected: checked.rejected.map((r) => ({ tool: r.call.function.name, detail: r.detail, name: nameOf(r.call) })), results: own, skipped: [...skipped, ...adapted.notices, ...checked.notices], issues: symbols.issues };
}
